/**
 * How the Keybook screens present each credential type — derived from THE one list (`@papan/keybook/core/field-match`:
 * `CREDENTIAL_TYPES` and each fixed type's stored fields), so a type or field added there is accepted by the API,
 * matched by the credential card, AND shown by these screens (#1324 owner review, I1). Only what the list can't say
 * lives here: a type's label, and a field's label and input kind.
 */
import { CREDENTIAL_TYPES, fixedCredentialFields, type KnownCredentialType } from '../core/field-match.js'
import type { KeybookEntryData } from '../core/types.js'
import { isFieldNameSensitive, staticFieldSensitivity } from '../core/field-sensitivity.js'

/** Each type's label (the page's type pill). Every type in `CREDENTIAL_TYPES` has one (pinned by a test). */
export const CREDENTIAL_TYPE_LABELS: Readonly<Record<KnownCredentialType, string>> = {
  password: 'Password',
  api_key: 'API Key',
  bearer: 'Bearer',
  connection_string: 'Connection',
  ssh_key: 'SSH Key',
  service_account: 'Service Account',
  oauth: 'OAuth',
  certificate: 'Certificate',
  github: 'GitHub',
  pat: 'PAT',
  custom: 'Custom',
}

/** A stored type's label, looked up as an OWN key only (#1346) — a row stored before the validation with
 *  `constructor` / `__proto__` reads its raw text, never Object's prototype. */
export function credentialTypeLabel(type: unknown): string {
  if (typeof type !== 'string') return ''
  return Object.prototype.hasOwnProperty.call(CREDENTIAL_TYPE_LABELS, type)
    ? CREDENTIAL_TYPE_LABELS[type as KnownCredentialType]
    : type
}

export type FieldInput = 'text' | 'password' | 'textarea'
/** Each fixed type's stored field, as the create dialog shows it (a test pins one entry per schema field). */
export const FIELD_UI: Readonly<Record<string, { label: string; input: FieldInput }>> = {
  username: { label: 'Username', input: 'text' },
  password: { label: 'Password', input: 'password' },
  key: { label: 'API Key', input: 'password' },
  token: { label: 'Token', input: 'password' },
  connection_string: { label: 'Connection String', input: 'password' },
  private_key: { label: 'Private Key', input: 'textarea' },
  json: { label: 'Service Account JSON', input: 'textarea' },
  certificate: { label: 'Certificate', input: 'textarea' },
  pat: { label: 'Personal Access Token', input: 'password' },
}

/** The create dialog's fields for a type: a fixed type's schema, in form order; anything else one secret `value`. */
export function getFieldsForType(type: string): { name: string; label: string; inputType: FieldInput }[] {
  const fixed = fixedCredentialFields(type)
  if (!fixed) return [{ name: 'value', label: 'Value', inputType: 'password' }]
  return fixed.map((f) => {
    const ui = Object.prototype.hasOwnProperty.call(FIELD_UI, f.name) ? FIELD_UI[f.name]! : undefined
    return { name: f.name, label: ui?.label ?? f.name, inputType: ui?.input ?? (f.kind === 'secret' ? 'password' : 'text') }
  })
}

/** Whether a field NAME implies a sensitive value — the default mask when an entry has no `fieldsMeta` for it. ONE
 *  rule with what a token's file reads are masked with (#1297): `core/field-sensitivity.ts`. */
export { isFieldNameSensitive } from '../core/field-sensitivity.js'

/** The detail panel's fields: a fixed type's schema (secrecy from the schema — no decrypt needed for labels); a GitHub
 *  key nothing (its token is server-managed); anything else from the decrypted payload, secrecy per `fieldsMeta`,
 *  else by name. */
export function getDisplayFieldsForType(credentialType: string, data?: KeybookEntryData | null): { name: string; sensitive: boolean }[] {
  const fixed = staticFieldSensitivity(credentialType)
  if (fixed) return fixed
  if (!data?.fields) return []
  return Object.keys(data.fields).map((name) => ({
    name,
    sensitive: data.fieldsMeta?.[name]?.sensitive ?? isFieldNameSensitive(name),
  }))
}

/** Types whose field shape is known from the type alone — no decrypt before rendering labels. `github` is not (its
 *  installation metadata is decrypted on selection); `custom` / `oauth` / unknown take their shape from the payload. */
export const STATIC_SCHEMA_TYPES: ReadonlySet<string> = new Set(CREDENTIAL_TYPES.filter((t) => t !== 'github' && fixedCredentialFields(t)))
